import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import cartService from "../services/cart";
import { addCouponToCart } from "../store/cart/actions";
import { removeCookie } from "../utils/cookies";

interface ProductToAdd {
  productOfferId?: string;
  planId?: string;
  quantity: number;
}

interface CartControllerProps {
  productsToAdd: ProductToAdd[];
  activeCart: boolean;
  isProcessing: boolean;
  dispatchAddProductsToCart: (
    products: ProductToAdd[],
    propertiesToTrack?: Record<string, any>,
  ) => Promise<void>;
  onClearProducts: () => void;
  fermatData?: {
    products: ProductToAdd[];
    discountCodes: string[];
    attribution: {
      vendor: string;
      sourceCode: string;
      checkoutId: string;
    };
  } | null;
}

const CartController: React.FC<CartControllerProps> = ({
  productsToAdd,
  activeCart,
  isProcessing,
  dispatchAddProductsToCart,
  onClearProducts,
  fermatData,
}) => {
  const dispatch = useDispatch();
  const [hasDispatched, setHasDispatched] = useState(false);

  useEffect(() => {
    if (
      !hasDispatched &&
      activeCart &&
      !isProcessing &&
      productsToAdd.length > 0
    ) {
      setHasDispatched(true);

      // Set flag that we're processing cart products
      sessionStorage.setItem("cart_processing", "true");

      // Add products to cart with Fermat attribution data if available
      const propertiesToTrack = fermatData?.attribution
        ? {
            fermat_vendor: fermatData.attribution.vendor,
            fermat_source_code: fermatData.attribution.sourceCode,
            fermat_checkout_id: fermatData.attribution.checkoutId,
          }
        : {};

      dispatchAddProductsToCart(productsToAdd, propertiesToTrack)
        .then(async () => {
          if (
            fermatData?.discountCodes &&
            fermatData.discountCodes.length > 0
          ) {
            try {
              const discountCode = fermatData.discountCodes[0];
              if (discountCode) {
                try {
                  await dispatch(addCouponToCart(discountCode));

                  sessionStorage.setItem(
                    "fermat_discount_applied",
                    discountCode,
                  );
                } catch (error) {
                  // Failed to apply discount code, but continue with cart processing
                  // Error is silently handled to avoid blocking cart functionality
                }
              }
            } catch (error) {
              // Failed to apply discount code, but continue with cart processing
              // Error is silently handled to avoid blocking cart functionality
            }
          }

          // Clear the Fermat cookie after successful cart processing
          if (fermatData) {
            // Mark that we've processed Fermat products in this session
            sessionStorage.setItem("fermat_products_processed", "true");

            // Clear the Fermat cookie using removeCookie with multiple option combinations
            // Try different combinations to ensure cookie is removed regardless of how it was set
            removeCookie("fermat_cart", { path: "/" });
            removeCookie("fermat_cart", {
              path: "/",
              domain: process.env.GATSBY_COOKIE_DOMAIN,
            });
          }

          // Clear the generic cart processing flag when complete
          sessionStorage.removeItem("cart_processing");

          cartService.openCart();
          onClearProducts();
        })
        .catch((error) => {
          // Handle error from adding products to cart
          // Error is silently handled to avoid blocking cart functionality

          // Clear cart processing flag on error
          sessionStorage.removeItem("cart_processing");

          onClearProducts();
        });
    }
  }, [
    hasDispatched,
    activeCart,
    isProcessing,
    productsToAdd,
    dispatchAddProductsToCart,
    onClearProducts,
    fermatData,
    dispatch,
  ]);

  return null;
};

export default CartController;
